/**
 * @fileoverview Data table component for displaying and managing rooms
 * @author Hospital Room Scheduler Team
 * @created 2025-01-08
 */

import React, { useState } from 'react';
import {
  Paper,
  Table,
  TextInput,
  Select,
  Group,
  Button,
  Text,
  Badge,
  ActionIcon,
  Menu,
  Pagination,
  Stack,
  Flex,
  Checkbox,
  Alert,
  LoadingOverlay,
  Tooltip,
  Modal,
  Title
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import {
  IconSearch,
  IconFilter,
  IconDots,
  IconEdit,
  IconTrash,
  IconEye,
  IconRefresh,
  IconAlertCircle,
  IconDoor,
  IconBuilding
} from '@tabler/icons-react';
import { RoomForm } from '../forms/RoomForm';
import type { RoomWithDetails, GetRoomsParams, UpdateRoomData } from '../../api/endpoints/rooms';
import type { Building } from '../../api/endpoints/buildings';

/**
 * Interface for pagination data
 */
interface PaginationData {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

/**
 * Props for RoomList component
 */
interface RoomListProps {
  rooms: RoomWithDetails[];
  pagination?: PaginationData;
  loading?: boolean;
  onFilterChange: (filters: Partial<GetRoomsParams>) => void;
  onSelectRoom: (roomId: string) => void;
  onUpdateRoom: (id: string, data: UpdateRoomData) => Promise<void>;
  onDeleteRoom: (id: string) => Promise<void>;
  buildings: Building[];
}

/**
 * Data table component for displaying rooms with search, filtering, and actions
 * Provides comprehensive room management interface with modern UX patterns
 */
export const RoomList: React.FC<RoomListProps> = ({
  rooms,
  pagination,
  loading = false,
  onFilterChange,
  onSelectRoom,
  onDeleteRoom,
  buildings
}) => {
  // State management
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchValue, 300);
  const [selectedBuilding, setSelectedBuilding] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [selectedRooms, setSelectedRooms] = useState<string[]>([]);
  const [editingRoom, setEditingRoom] = useState<string | null>(null);
  const [deletingRoom, setDeletingRoom] = useState<string | null>(null);

  // Apply search filter
  React.useEffect(() => {
    onFilterChange({ search: debouncedSearch });
  }, [debouncedSearch, onFilterChange]);

  /**
   * Handle search input change
   * @param event - Input change event
   */
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.currentTarget.value);
  };

  /**
   * Handle building filter change
   * @param value - Selected building ID
   */
  const handleBuildingFilterChange = (value: string | null) => {
    const buildingId = value || '';
    setSelectedBuilding(buildingId);
    if (buildingId) {
      onFilterChange({ buildingId });
    } else {
      onFilterChange({});
    }
  };

  /**
   * Handle status filter change
   * @param value - Selected status
   */
  const handleStatusFilterChange = (value: string | null) => {
    const status = value || '';
    setStatusFilter(status);
    if (status === 'active') {
      onFilterChange({ isActive: true });
    } else if (status === 'inactive') {
      onFilterChange({ isActive: false });
    } else {
      onFilterChange({});
    }
  };

  /**
   * Handle sort change
   * @param field - Field to sort by
   */
  const handleSortChange = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    onFilterChange({ sortBy: field as any, sortOrder });
  };

  /**
   * Handle page change
   * @param page - New page number
   */
  const handlePageChange = (page: number) => {
    onFilterChange({ page });
  };

  /**
   * Handle room selection
   * @param roomId - Room ID
   * @param checked - Whether room is selected
   */
  const handleRoomSelect = (roomId: string, checked: boolean) => {
    setSelectedRooms(prev => 
      checked 
        ? [...prev, roomId]
        : prev.filter(id => id !== roomId)
    );
  };

  /**
   * Handle select all rooms
   * @param checked - Whether all rooms are selected
   */
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRooms(rooms.map(room => room.id));
    } else {
      setSelectedRooms([]);
    }
  };

  /**
   * Handle room edit
   * @param roomId - Room ID to edit
   */
  const handleEditRoom = (roomId: string) => {
    setEditingRoom(roomId);
  };

  /**
   * Handle room delete confirmation
   * @param roomId - Room ID to delete
   */
  const handleDeleteRoom = (roomId: string) => {
    setDeletingRoom(roomId);
  };

  /**
   * Confirm room deletion
   */
  const confirmDeleteRoom = async () => {
    if (deletingRoom) {
      await onDeleteRoom(deletingRoom);
      setDeletingRoom(null);
    }
  };

  /**
   * Get room status badge
   * @param room - Room data
   */
  const getRoomStatusBadge = (room: RoomWithDetails) => {
    return (
      <Badge
        color={room.isActive ? 'green' : 'red'}
        variant="light"
        size="sm"
      >
        {room.isActive ? 'Active' : 'Inactive'}
      </Badge>
    );
  };

  /**
   * Get capacity badge
   * @param capacity - Room capacity
   */
  const getCapacityBadge = (capacity: number) => {
    const color = capacity >= 10 ? 'blue' : capacity >= 5 ? 'green' : 'gray';
    return (
      <Badge color={color} variant="light" size="sm">
        {capacity} person{capacity > 1 ? 's' : ''}
      </Badge>
    );
  };

  // Clear search and filters
  const clearFilters = () => {
    setSearchValue('');
    setSelectedBuilding('');
    setStatusFilter('');
    onFilterChange({});
  };

  return (
    <>
      <Paper radius="md" withBorder>
        <LoadingOverlay visible={loading} />
      
      {/* Header with filters */}
      <Paper p="md" radius="md" withBorder>
        <Stack gap="md">
          <Group justify="space-between" align="center">
            <Title order={5} fw={600}>
              Rooms ({pagination?.total || 0})
            </Title>
            
            <Group gap="sm">
              <Button
                variant="outline"
                size="xs"
                leftSection={<IconRefresh size="0.875rem" />}
                onClick={() => window.location.reload()}
              >
                Refresh
              </Button>
            </Group>
          </Group>

          {/* Filters */}
          <Group gap="md" align="flex-end">
            <TextInput
              placeholder="Search rooms..."
              leftSection={<IconSearch size="1rem" />}
              value={searchValue}
              onChange={handleSearchChange}
              style={{ flex: 1 }}
            />
            
            <Select
              placeholder="All Buildings"
              data={[
                { value: '', label: 'All Buildings' },
                ...buildings.map(building => ({
                  value: building.id,
                  label: building.name
                }))
              ]}
              value={selectedBuilding}
              onChange={handleBuildingFilterChange}
              leftSection={<IconBuilding size="1rem" />}
              clearable
            />
            
            <Select
              placeholder="All Status"
              data={[
                { value: '', label: 'All Status' },
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' }
              ]}
              value={statusFilter}
              onChange={handleStatusFilterChange}
              leftSection={<IconFilter size="1rem" />}
              clearable
            />
            
            <Button
              variant="outline"
              size="xs"
              onClick={clearFilters}
              disabled={!searchValue && !selectedBuilding && !statusFilter}
            >
              Clear
            </Button>
          </Group>

          {/* Bulk actions */}
          {selectedRooms.length > 0 && (
            <Alert
              color="blue"
              variant="light"
              icon={<IconAlertCircle size="1rem" />}
            >
              <Group justify="space-between" align="center">
                <Text size="sm">
                  {selectedRooms.length} room{selectedRooms.length > 1 ? 's' : ''} selected
                </Text>
                <Group gap="xs">
                  <Button size="xs" variant="outline">
                    Bulk Edit
                  </Button>
                  <Button size="xs" variant="outline" color="red">
                    Bulk Delete
                  </Button>
                </Group>
              </Group>
            </Alert>
          )}
        </Stack>
      </Paper>

      {/* Table */}
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th style={{ width: '40px' }}>
              <Checkbox
                checked={selectedRooms.length === rooms.length && rooms.length > 0}
                indeterminate={selectedRooms.length > 0 && selectedRooms.length < rooms.length}
                onChange={(event) => handleSelectAll(event.currentTarget.checked)}
              />
            </Table.Th>
            <Table.Th>
              <Button
                variant="subtle"
                size="xs"
                onClick={() => handleSortChange('number')}
                rightSection={sortBy === 'number' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
              >
                Room
              </Button>
            </Table.Th>
            <Table.Th>
              <Button
                variant="subtle"
                size="xs"
                onClick={() => handleSortChange('name')}
                rightSection={sortBy === 'name' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
              >
                Name
              </Button>
            </Table.Th>
            <Table.Th>
              <Button
                variant="subtle"
                size="xs"
                onClick={() => handleSortChange('capacity')}
                rightSection={sortBy === 'capacity' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
              >
                Capacity
              </Button>
            </Table.Th>
            <Table.Th>Location</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th style={{ width: '100px' }}>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        
        <Table.Tbody>
          {rooms.map((room) => (
            <Table.Tr key={room.id}>
              <Table.Td>
                <Checkbox
                  checked={selectedRooms.includes(room.id)}
                  onChange={(event) => handleRoomSelect(room.id, event.currentTarget.checked)}
                />
              </Table.Td>
              
              <Table.Td>
                <Group gap="xs">
                  <IconDoor size="1rem" color="var(--mantine-color-blue-6)" />
                  <Text fw={500}>{room.number}</Text>
                </Group>
              </Table.Td>
              
              <Table.Td>
                <Text size="sm">{room.name}</Text>
                {room.description && (
                  <Text size="xs" c="dimmed" truncate>
                    {room.description}
                  </Text>
                )}
              </Table.Td>
              
              <Table.Td>
                {getCapacityBadge(room.capacity)}
              </Table.Td>
              
              <Table.Td>
                <Stack gap={2}>
                  <Group gap="xs">
                    <IconBuilding size="0.875rem" color="var(--mantine-color-gray-6)" />
                    <Text size="xs" c="dimmed">
                      {room.floor.building.name}
                    </Text>
                  </Group>
                  <Text size="xs" c="dimmed">
                    Floor {room.floor.number} - {room.floor.name}
                  </Text>
                </Stack>
              </Table.Td>
              
              <Table.Td>
                {getRoomStatusBadge(room)}
              </Table.Td>
              
              <Table.Td>
                <Group gap="xs">
                  <Tooltip label="View Details">
                    <ActionIcon
                      variant="subtle"
                      size="sm"
                      onClick={() => onSelectRoom(room.id)}
                    >
                      <IconEye size="0.875rem" />
                    </ActionIcon>
                  </Tooltip>
                  
                  <Menu shadow="md" width={200}>
                    <Menu.Target>
                      <ActionIcon variant="subtle" size="sm">
                        <IconDots size="0.875rem" />
                      </ActionIcon>
                    </Menu.Target>
                    
                    <Menu.Dropdown>
                      <Menu.Item
                        leftSection={<IconEdit size="1rem" />}
                        onClick={() => handleEditRoom(room.id)}
                      >
                        Edit Room
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconTrash size="1rem" />}
                        color="red"
                        onClick={() => handleDeleteRoom(room.id)}
                      >
                        Delete Room
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <Flex justify="center" p="md">
          <Pagination
            value={pagination.page}
            onChange={handlePageChange}
            total={pagination.totalPages}
            size="sm"
          />
        </Flex>
      )}

      {/* Empty state */}
      {rooms.length === 0 && !loading && (
        <Paper p="xl" ta="center">
          <Stack gap="md" align="center">
            <IconDoor size="3rem" color="var(--mantine-color-gray-4)" />
            <div>
              <Text size="lg" fw={500} c="dimmed">
                No rooms found
              </Text>
              <Text size="sm" c="dimmed">
                {searchValue || selectedBuilding || statusFilter
                  ? 'Try adjusting your search filters'
                  : 'Get started by creating your first room'}
              </Text>
            </div>
          </Stack>
        </Paper>
      )}
    </Paper>

      {/* Edit Room Modal */}
      {editingRoom && (
        <RoomForm
          opened={!!editingRoom}
          onClose={() => setEditingRoom(null)}
          onSubmit={async () => {}}
          roomId={editingRoom}
          buildings={buildings}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        opened={!!deletingRoom}
        onClose={() => setDeletingRoom(null)}
        title="Delete Room"
        centered
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to delete this room? This action cannot be undone.
          </Text>
          
          <Group justify="flex-end" gap="sm">
            <Button
              variant="outline"
              onClick={() => setDeletingRoom(null)}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={confirmDeleteRoom}
            >
              Delete Room
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};
